import { Clock } from 'lucide-react'

import { aboutTime, DEFAULT_LENGTHS, type StoryLength, type StoryLengthPreset } from '@/api/stories'
import { cn } from '@/components/ui/utils'

/**
 * "How long?" in the wizard's last step: Short (one part, about 30 seconds), Medium (about 2
 * minutes in 4 parts) or Long (about 3–5 minutes in 6–8 parts). Below the choice, in plain
 * words, how long making it usually takes and — once the studio has made a few — what it costs.
 * The choices come from `GET /api/story-presets` → `lengths` (`DEFAULT_LENGTHS` when an older
 * server leaves them out).
 */

export interface LengthPickerProps {
  value: StoryLength
  onChange(length: StoryLength): void
  lengths?: StoryLengthPreset[] | null
}

/** "$0.35" for a known cost, else null. */
export function costLabel(usd: number | null | undefined): string | null {
  if (usd == null || !Number.isFinite(usd) || usd <= 0) return null
  return `$${usd < 0.01 ? usd.toFixed(3) : usd.toFixed(2)}`
}

/**
 * The sentence under the choice: "Takes about 1–3 minutes to make · usually about $0.35." A longer
 * story plays while the rest is made, so when the server says how soon (`starts_s`) that is said
 * too: "Takes about 2–4 minutes to make; preview the first part in about 50 seconds."
 */
export function lengthHint(item: StoryLengthPreset): string {
  const cost = costLabel(item.usd)
  const make = `Takes ${aboutTime(item.make_s)} to make`
  const starts = item.starts_s && item.parts[1] > 1 ? `; preview the first part in ${aboutTime(item.starts_s)}` : ''
  const parts = item.parts[1] > 1 ? ' It plays in parts, like the chapters of a book.' : ''
  return `${make}${starts}${cost ? ` · usually about ${cost}` : ''}.${parts}`
}

export function LengthPicker({ value, onChange, lengths }: LengthPickerProps) {
  const items = lengths && lengths.length > 0 ? lengths : DEFAULT_LENGTHS
  const chosen = items.find((l) => l.id === value) ?? items[0]
  return (
    <div className="flex flex-col gap-2" data-testid="length-picker">
      <span className="text-sm font-medium text-ink" id="length-label">
        How long?
      </span>
      <div role="radiogroup" aria-labelledby="length-label" className="grid max-w-xl grid-cols-1 gap-2 sm:grid-cols-3">
        {items.map((item) => {
          const on = item.id === value
          return (
            <button
              key={item.id}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`${item.label}, ${item.blurb}`}
              onClick={() => onChange(item.id)}
              className={cn(
                'flex min-h-16 flex-col items-start justify-center gap-0.5 rounded-xl border px-3 py-2 text-left transition-colors focus-visible:focus-ring',
                on ? 'border-transparent bg-accent text-accent-ink' : 'border-line bg-paper text-ink hover:bg-paper-2',
              )}
            >
              <span className="text-sm font-semibold">{item.label}</span>
              <span className={cn('text-xs', on ? 'text-accent-ink/90' : 'text-ink-2')}>{item.blurb}</span>
            </button>
          )
        })}
      </div>
      {chosen ? (
        <p className="flex items-start gap-1.5 text-xs text-ink-2" data-testid="length-hint" aria-live="polite">
          <Clock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>{lengthHint(chosen)}</span>
        </p>
      ) : null}
    </div>
  )
}
